import { z } from 'zod';

import { HttpClient } from '~/src/clients/http/HttpClient';
import { djangoHttpClient } from '~/src/clients/shippo/DjangoHttpClient';

import {
  achDiscountTokens,
  creditCardConveienceFeeTokens,
} from '../views/Account/pages/Billing/constants/billing';

export const BUSINESS_TYPE_OPTIONS = [
  {
    caption: 'businessTypeModal.directShipperCaption',
    text: 'businessTypeModal.directShipper',
    value: 'DIRECT_SHIPPER',
  },
  {
    caption: 'businessTypeModal.defaultCaption',
    text: 'businessTypeModal.eCommercePlatform',
    value: 'ECOMMERCE_PLATFORM',
  },
  {
    caption: 'businessTypeModal.defaultCaption',
    text: 'businessTypeModal.marketplace',
    value: 'MARKETPLACE',
  },
  {
    caption: 'businessTypeModal.defaultCaption',
    text: 'businessTypeModal.wms',
    value: 'WMS_IMS_OMS',
  },
  {
    caption: 'businessTypeModal.3plCaption',
    text: 'businessTypeModal.3pl',
    value: '3PL',
  },
] as const;

export type BusinessType = (typeof BUSINESS_TYPE_OPTIONS)[number]['value'];

const businessTypeValues = BUSINESS_TYPE_OPTIONS.map((option) => option.value) as [BusinessType];
export const BUSINESS_TYPE_SCHEMA = z.enum(businessTypeValues);

const USER_ACCOUNT_SCHEMA = z.object({
  address: z
    .object({
      city: z.string(),
      company: z.string(),
      country: z.nullable(z.string()),
      email: z.string(),
      id: z.number(),
      is_default_return: z.boolean(),
      is_default_sender: z.boolean(),
      name: z.string(),
      object_created: z.string().nullable(),
      object_updated: z.string(),
      phone: z.string(),
      state: z.string(),
      street_no: z.string(),
      street1: z.string(),
      street2: z.string(),
      zip: z.string(),
    })
    .optional(),
  api_key_generation_enabled: z.boolean().optional(),
  available_credit: z
    .array(
      z.object({
        amount: z.string(),
        currency: z.string(),
      }),
    )
    .optional(),
  business_type: BUSINESS_TYPE_SCHEMA.nullable().optional(),
  current_promo: z
    .object({
      amount: z.number(),
      code: z.string(),
      currency: z.string(),
      end_date: z.string(),
      max_uses: z.number(),
      min_qualify_units: z.number(),
      offer_text: z.string(),
      start_date: z.string(),
      status: z.string(),
      terms: z.string().nullable(),
      unit_type: z.number(),
      units: z.number(),
    })
    .nullable()
    .optional(),
  date_joined: z.string(),
  email: z.string().email(),
  federated_emails: z.array(z.string()),
  first_name: z.string(),
  has_failed_payment: z.boolean(),
  has_purchased_label: z.boolean(),
  id: z.number(),
  invoice_adjustments: z.array(z.string()),
  is_account_owner: z.boolean(),
  is_active: z.boolean(),
  is_password_login_allowed: z.boolean(),
  is_prepaid: z.boolean().optional(),
  last_name: z.string(),
  lifetime_vol_segment_max: z.number(),
  lifetime_vol_segment_min: z.number(),
  login_user_id: z.number(),
  payment_type: z.string().optional(),
  requires_email_verification: z.boolean(),
  revenue_tier_name: z.string(),
  username: z.string(),
});

const UPDATE_USER_ACCOUNT_REQUEST_BASE = z.object({
  email: z.string().email().optional(),
  name: z.string().optional(),
  new_password: z.string().optional(),
  new_password_confirmation: z.string().optional(),
});

const UPDATE_USER_ACCOUNT_REQUEST_WITH_PASSWORD = UPDATE_USER_ACCOUNT_REQUEST_BASE.merge(
  z.object({ password: z.string() }),
);

const UPDATE_USER_ACCOUNT_REQUEST_WITH_FEDERATED = UPDATE_USER_ACCOUNT_REQUEST_BASE.merge(
  z.object({
    auth_code: z.string(),
    provider: z.string(),
    state: z.string(),
  }),
);

const UPDATE_USER_ACCOUNT_REQUEST_SCHEMA = z.union([
  UPDATE_USER_ACCOUNT_REQUEST_WITH_PASSWORD,
  UPDATE_USER_ACCOUNT_REQUEST_WITH_FEDERATED,
]);

const PROFILE_USER_RESPONSE_SCHEMA = z.object({
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
});

type ApiProfileUserResponse = z.infer<typeof PROFILE_USER_RESPONSE_SCHEMA>;
type ApiUserAccount = z.infer<typeof USER_ACCOUNT_SCHEMA>;

export type UserAccount = {
  achDiscountToken?: string; // grabs the first 'achDiscountTokens' from invoice_adjustments
  address?: {
    city: string;
    company: string;
    country: null | string;
    email: string;
    id: number;
    isDefaultReturn: boolean;
    isDefaultSender: boolean;
    name: string;
    objectCreated: null | string;
    objectUpdated: string;
    phone: string;
    state: string;
    street1: string;
    street2: string;
    streetNo: string;
    zip: string;
  };
  apiKeyGenerationEnabled?: boolean;
  availableCredits?: {
    amount: string;
    currency: string;
  }[];
  businessType: BusinessType | null;
  creditCardConvenienceFeeToken?: string; // grabs the first 'creditCardConveienceFeeTokens' from invoice_adjustments
  currentPromo?: {
    amount: number;
    code: string;
    currency: string;
    endDate: string;
    maxUses: number;
    minQualifyUnits: number;
    offerText: string;
    startDate: string;
    status: string;
    terms: null | string;
    unitType: number;
    units: number;
  };
  dateJoined: string;
  email: string;
  federatedEmails: string[];
  firstName: string;
  hasFailedPayment: boolean;
  hasPurchasedLabel: boolean;
  id: number;
  isAccountOwner: boolean;
  isActive: boolean;
  isPasswordLoginAllowed: boolean;
  isPrePaid?: boolean;
  lastName: string;
  lifetimeVolSegmentMax: number;
  lifetimeVolSegmentMin: number;
  loginUserId: number;
  paymentType?: string;
  requiresEmailVerification: boolean;
  revenueTierName: string;
  username: string;
};

export type ProfileUpdateAuth =
  | { authCode: string; provider: string; state: string }
  | { password: string };

export type UpdateUserAccount = {
  email?: string;
  name?: string;
  newPassword?: string;
  newPasswordConfirmation?: string;
} & ProfileUpdateAuth;

export type ProfileUserAccount = {
  email: string;
  firstName: string;
  lastName: string;
};

class UserAccountService {
  private parseProfileUserResponse = (body: ApiProfileUserResponse): ProfileUserAccount => ({
    email: body.email,
    firstName: body.first_name,
    lastName: body.last_name,
  });

  private parseUpdateUserAccountRequest = (
    data: UpdateUserAccount,
  ): z.infer<typeof UPDATE_USER_ACCOUNT_REQUEST_SCHEMA> => {
    const base = {
      email: data.email,
      name: data.name,
      new_password: data.newPassword,
      new_password_confirmation: data.newPasswordConfirmation,
    };
    if ('password' in data) return { ...base, password: data.password };

    return {
      ...base,
      auth_code: data.authCode,
      provider: data.provider,
      state: data.state,
    };
  };

  private parseUserAccount = (body: ApiUserAccount): UserAccount => ({
    achDiscountToken: body.invoice_adjustments.find(
      (adjustment) => !!achDiscountTokens[adjustment as keyof typeof achDiscountTokens],
    ),
    address: body.address
      ? {
          city: body.address.city,
          company: body.address.company,
          country: body.address.country,
          email: body.address.email,
          id: body.address.id,
          isDefaultReturn: body.address.is_default_return,
          isDefaultSender: body.address.is_default_sender,
          name: body.address.name,
          objectCreated: body.address.object_created,
          objectUpdated: body.address.object_updated,
          phone: body.address.phone,
          state: body.address.state,
          street1: body.address.street1,
          street2: body.address.street2,
          streetNo: body.address.street_no,
          zip: body.address.zip,
        }
      : undefined,
    apiKeyGenerationEnabled: body.api_key_generation_enabled,
    availableCredits: body.available_credit,
    businessType: body.business_type ?? null,
    creditCardConvenienceFeeToken: body.invoice_adjustments.find(
      (adjustment) =>
        !!creditCardConveienceFeeTokens[adjustment as keyof typeof creditCardConveienceFeeTokens],
    ),
    currentPromo: body.current_promo
      ? {
          amount: body.current_promo.amount,
          code: body.current_promo.code,
          currency: body.current_promo.currency,
          endDate: body.current_promo.end_date,
          maxUses: body.current_promo.max_uses,
          minQualifyUnits: body.current_promo.min_qualify_units,
          offerText: body.current_promo.offer_text,
          startDate: body.current_promo.start_date,
          status: body.current_promo.status,
          terms: body.current_promo.terms,
          unitType: body.current_promo.unit_type,
          units: body.current_promo.units,
        }
      : undefined,
    dateJoined: body.date_joined,
    email: body.email,
    federatedEmails: body.federated_emails,
    firstName: body.first_name,
    hasFailedPayment: body.has_failed_payment,
    hasPurchasedLabel: body.has_purchased_label,
    id: body.id,
    isAccountOwner: body.is_account_owner,
    isActive: body.is_active,
    isPasswordLoginAllowed: body.is_password_login_allowed,
    isPrePaid: body.is_prepaid,
    lastName: body.last_name,
    lifetimeVolSegmentMax: body.lifetime_vol_segment_max,
    lifetimeVolSegmentMin: body.lifetime_vol_segment_min,
    loginUserId: body.login_user_id,
    paymentType: body.payment_type,
    requiresEmailVerification: body.requires_email_verification,
    revenueTierName: body.revenue_tier_name,
    username: body.username,
  });

  get = async () => {
    const { body } = await this.httpClient.get<UserAccount>({
      url: '/v1/ui/account',
    });

    const apiUser = USER_ACCOUNT_SCHEMA.parse(body);

    return this.parseUserAccount(apiUser);
  };

  resendEmailVerification = async () => {
    // this endpoint always returns 200 with an empty body
    await this.httpClient.get({
      url: '/verify/resend/',
    });
  };

  saveBusinessType = async (businessType: BusinessType | null) => {
    const { body } = await this.httpClient.patch<{ business_type: BusinessType }>({
      body: {
        business_type: businessType,
      },
      url: '/v1/ui/account',
    });

    const userAccount = USER_ACCOUNT_SCHEMA.parse(body);

    return this.parseUserAccount(userAccount);
  };

  updateUser = async (data: UpdateUserAccount) => {
    const { body } = await this.httpClient.put<ProfileUserAccount>({
      body: this.parseUpdateUserAccountRequest(data),
      url: '/ui/user/profile',
      withCredentials: true,
    });

    const apiUser = PROFILE_USER_RESPONSE_SCHEMA.parse(body);

    return this.parseProfileUserResponse(apiUser);
  };

  constructor(private readonly httpClient: HttpClient = djangoHttpClient) {}
}

export const userAccountService = new UserAccountService();
